export enum MedicalDetailType {
  BLOOD_TYPE = 'bloodType',
  ALLERGIES = 'allergies',
  CONDITIONS = 'conditions',
  HABITS = 'habits',
  ORGAN_TRANPLANTS = 'organTransplants',
  DEVICE_IMPLANTS = 'deviceImplants',
  VACCINE_TRACKER = 'vaccineTracker',
  HOSPITAL_HISTORY = 'hospitalHistory',
  SURGERIES = 'surgeries',
  BIRTH_COMPLICATIONS = 'birthComplications',
  PET_CARE = 'petCare',
  MEDICINE = 'medicine',
  NOTE = 'note',
}

export enum MedicalDetailStatus {
  NOT_ANSWERED = 'notAnswered',
  NONE = 'none',
  ANSWERED = 'answered',
}

export interface IMedicalDetail {
  status: MedicalDetailStatus;
  count?: number;
  value?: string;
}

export enum BloodType {
  A_POSITIVE = 'A+',
  A_NEGATIVE = 'A-',
  B_POSITIVE = 'B+',
  B_NEGATIVE = 'B-',
  AB_POSITIVE = 'AB+',
  AB_NEGATIVE = 'AB-',
  O_POSITIVE = 'O+',
  O_NEGATIVE = 'O-',
}

// --- Cat Blood Types ---
export enum CatBloodType {
  A = 'A',
  B = 'B',
  AB = 'AB',
}

// --- Dog Blood Types ---
export enum DogBloodType {
  DEA_1_1_POS = 'DEA 1.1+',
  DEA_1_1_NEG = 'DEA 1.1-',
  DEA_1_2 = 'DEA 1.2',
  DEA_1_3 = 'DEA 1.3',
  DEA_3 = 'DEA 3',
  DEA_4 = 'DEA 4',
  DEA_5 = 'DEA 5',
  DEA_6 = 'DEA 6',
  DEA_7 = 'DEA 7',
  DEA_8 = 'DEA 8',
}

export interface HealthProfile {
  bloodType?: BloodType;
  notes?: string;
  allergies?: string[];
  // Add additional health profile fields as needed
  deviceImplants?: IDeviceImplant[];
  birthComplications?: Record<BirthComplications, IBirthComplication>;
}

export interface HealthProfileSummary {
  summary: Record<MedicalDetailType, IMedicalDetail>;
}

export interface IDeviceImplant {
  name: string;
  date: string; // ISO string
}

export const deviceImplantCategory = 'implanted-devices';

export const allergyTypes = [
  { label: 'Food', value: 'food' },
  { label: 'Drugs', value: 'drugs' },
  { label: 'Any Other', value: 'other' },
];

export const allergyCategoryMap: Record<string, string> = {
  food: 'food_allergies',
  drugs: 'drug_allergies',
  other: 'other_allergies',
};

export const conditionCategory = 'conditions';

export interface ICondition {
  name: string;
  diagnosisDate: string; // ISO string
}

export interface ISurgery {
  name: string;
  date: string; // ISO string
}

export const surgeryCategory = 'surgeries';

// --- Birth Complications ---

export const BIRTH_COMPLICATIONS_QUESTIONS = [
  {
    key: 'prematureBirth',
    label: 'Was your child born before 37 weeks of pregnancy?',
  },
  {
    key: 'lowBirthWeight',
    label: 'Was their birth weight less than 2.5 kg?',
  },
  {
    key: 'complications',
    label: 'Were there any birth-related complications?',
  },
] as const;

export const BIRTH_COMPLICATIONS_OPTIONS = [
  { value: false, label: 'No' },
  { value: true, label: 'Yes' },
  { value: null, label: 'Not sure' },
] as const;

export const weekdaysOptions = [
  { value: 'Mon', label: 'Monday' },
  { value: 'Tue', label: 'Tuesday' },
  { value: 'Wed', label: 'Wednesday' },
  { value: 'Thu', label: 'Thursday' },
  { value: 'Fri', label: 'Friday' },
  { value: 'Sat', label: 'Saturday' },
  { value: 'Sun', label: 'Sunday' },
];

export enum BirthComplications {
  PREMATURE_BIRTH = 'prematureBirth',
  LOW_BIRTH_WEIGHT = 'lowBirthWeight',
  COMPLICATIONS = 'complications',
}

export enum BirthComplicationsStatus {
  NO = 'no',
  YES = 'yes',
  NOT_SURE = 'not_sure',
}

export interface IBirthComplication {
  status: BirthComplicationsStatus | null;
  content?: string[];
}

export const vaccinationCategory = 'vaccinations';
